import React, {useCallback, useEffect, useMemo} from 'react';
import {FormikHelpers, useFormikContext} from 'formik';
import {PageLayoutTwoVerticalCols} from 'layout';
import {Form} from 'forms';
import {UploadFileHeader, UploadFileFooter, UploadFileContentMiddle, UploadFileContentStart} from './components';
import {FormNameEnum} from '../enums';
import {IUploadFileFormBaseValues, mapUploadResourceFile, uploadFileFormBaseValidationSchema} from 'admin/resources';
import {useUploadFile} from './hooks';
import {usePermissions} from 'permissions';
import {SuperAndNationalAdminroles} from 'enums/admin-roles';
import {useLanguages} from '../hooks';
import {usePrefetchResourceMetadataLookups} from 'filters-selections';
import {
  ResourceFormikBridge,
  runResourceFormSubmit,
  useResourceMetadataLookupNames,
  useResourceUploadFormLifecycle,
} from 'admin/resources/utils';

const SeedDefaultLanguage = () => {
  const {englishId} = useLanguages();
  const {values, setFieldValue} = useFormikContext<IUploadFileFormBaseValues>();

  useEffect(() => {
    if (!englishId) {
      return;
    }

    const languages = values?.[FormNameEnum.languages];
    if (!languages || languages.length === 0) {
      setFieldValue(FormNameEnum.languages, [englishId], false);
    }
    // Only seed when the lookup id arrives; do not re-run on every form change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [englishId, setFieldValue]);

  return null;
};

export const UploadResourceFile = () => {
  const {uploadFile, submitStatus} = useUploadFile();
  const {hasPermission} = usePermissions();
  const {englishId} = useLanguages();
  const hasAdminPermission = hasPermission(SuperAndNationalAdminroles);
  const lookupNames = useResourceMetadataLookupNames();

  usePrefetchResourceMetadataLookups();

  const initialValues = useMemo(
    () => ({
      [FormNameEnum.file]: null,
      [FormNameEnum.isAccessibleToStudents]: null,
      [FormNameEnum.hasUploadPermissions]: hasAdminPermission ? true : false,
      [FormNameEnum.displayName]: null,
      [FormNameEnum.keyTerms]: null,
      [FormNameEnum.programs]: [],
      [FormNameEnum.sessions]: [],
      [FormNameEnum.pillars]: [],
      [FormNameEnum.competencies]: [],
      [FormNameEnum.gradeLevels]: [],
      [FormNameEnum.languages]: englishId ? [englishId] : [],
      [FormNameEnum.skillsAndBehaviors]: [],
      [FormNameEnum.briefDescription]: '',
      [FormNameEnum.learningObjectives]: null,
      [FormNameEnum.fileType]: null,
      [FormNameEnum.expirationDate]: null,
      [FormNameEnum.knowledgeSkillsAndAbilities]: [],
      [FormNameEnum.shouldDisableFileType]: false,

      [FormNameEnum.id]: null,
      [FormNameEnum.fileName]: null,
      [FormNameEnum.link]: null,

      [FormNameEnum.stockImageLinkNumber]: null,
      [FormNameEnum.permissionNeeded]: null,
      [FormNameEnum.imageSources]: [],
      [FormNameEnum.ethnicities]: [],
      [FormNameEnum.genders]: [],
      [FormNameEnum.publishedOutputTool]: null,
      [FormNameEnum.alternateText]: null,
      [FormNameEnum.linkToStockImageSource]: null,

      [FormNameEnum.slideNumber]: null,
      [FormNameEnum.sessionThemeNumber]: null,

      [FormNameEnum.associatedResources]: [],
      [FormNameEnum.numberOfResources]: null,
      [FormNameEnum.resourceIds]: [],

      [FormNameEnum.characterTraits]: [],
      [FormNameEnum.behavioralIndicators]: [],
      [FormNameEnum.videoTranscriptionLink]: null,
      [FormNameEnum.credentialing]: null,
      [FormNameEnum.permissionInPerpetuity]: false,
      [FormNameEnum.accessibilityStatus]: null,
      [FormNameEnum.accessibilityReports]: null,

      [FormNameEnum.urlSource]: null,
      [FormNameEnum.usageType]: null,
      [FormNameEnum.reuseFlag]: null,
      [FormNameEnum.notes]: null,
      [FormNameEnum.permissionAgreementUrl]: null,
    }),
    [hasAdminPermission, englishId]
  );

  const {formKey, capturePreSubmit, registerFormik} = useResourceUploadFormLifecycle<IUploadFileFormBaseValues>(
    initialValues as IUploadFileFormBaseValues,
    submitStatus,
    'file'
  );

  const submitHandler = useCallback(
    (formValues: IUploadFileFormBaseValues, helpers: FormikHelpers<IUploadFileFormBaseValues>) => {
      capturePreSubmit(formValues);
      const payload = mapUploadResourceFile(formValues, lookupNames);

      return runResourceFormSubmit(formValues, helpers, () => uploadFile(payload));
    },
    [uploadFile, lookupNames, capturePreSubmit]
  );

  const contentMiddle = useMemo(() => <UploadFileContentMiddle />, []);
  const contentStart = useMemo(() => <UploadFileContentStart />, []);

  return (
    <Form.Form<IUploadFileFormBaseValues>
      key={formKey}
      initialValues={initialValues as IUploadFileFormBaseValues}
      validationSchema={uploadFileFormBaseValidationSchema}
      submitHandler={submitHandler}
      enableReinitialize={false}
      disableNativeSubmit
    >
      <ResourceFormikBridge registerFormik={registerFormik} />
      <SeedDefaultLanguage />
      <PageLayoutTwoVerticalCols
        header={<UploadFileHeader />}
        contentStart={contentStart}
        contentMiddle={contentMiddle}
        withDoubleFirst={false}
        withDoubleSecond
        withFooter
        footer={<UploadFileFooter />}
        withSecondaryNavigation
      />
    </Form.Form>
  );
};
